import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import './App.css';
import { useChat } from './hooks/useChat';
import { useAuth } from './hooks/useAuth';
import { loadUserPreferences } from './utils/userUtils';
import { PROJECTS, DEFAULT_PROJECT } from './config/projects';

import Sidebar from './components/Sidebar';
import Header from './components/Header';
import LoginScreen from './components/LoginScreen';
import LoadingScreen from './components/LoadingScreen';
import WelcomeSection from './components/WelcomeSection';
import ChatContainer from './components/ChatContainer';
import ChatFooter from './components/ChatFooter';
import RightRail from './components/RightRail';
import { Box, Paper, IconButton, Menu, MenuItem } from '@mui/material'; // add MUI wrappers
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddPeopleModal from './components/AddPeopleModal';
import ViewPeopleModal from './components/ViewPeopleModal';

function App() {
  // Authentication
  const { 
    isAuthenticated, 
    userInfo, 
    isAdmin, 
    authLoading, 
    inProgress, 
    handleSignIn, 
    handleSignOut 
  } = useAuth();
  
  // Chat integration
  const { messages, isLoading, error, sendMessage, clearChat, startNewConversation, loadConversation } = useChat();
  
  // UI state
  const [input, setInput] = useState('');
  const [selectedDataSource, setSelectedDataSource] = useState(DEFAULT_PROJECT);
  const [availableSources, setAvailableSources] = useState(PROJECTS);
  const [cardMaxHeight, setCardMaxHeight] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsOpen = Boolean(actionsAnchorEl);
  const [showAddPeople, setShowAddPeople] = useState(false);
  const [showViewPeople, setShowViewPeople] = useState(false);
  
  const chatContainerRef = useRef(null);
  const cardRef = useRef(null); 

  const GAP_ABOVE_BOTTOM = 20; // small gap at bottom of viewport

  const handleActionsClick = (e) => setActionsAnchorEl(e.currentTarget);
  const handleActionsClose = () => setActionsAnchorEl(null);

  const handleDownload = () => { handleActionsClose(); /* placeholder */ };
  const handleAddPeople = () => { setShowAddPeople(true); handleActionsClose(); };
  const handleViewPeople = () => { setShowViewPeople(true); handleActionsClose(); };
  const handleCloseAddPeople = () => setShowAddPeople(false);
  const handleCloseViewPeople = () => setShowViewPeople(false);

  // Load user preferences
  useEffect(() => {
    if (isAuthenticated && userInfo?.email) {
      loadUserPreferences(userInfo.email, setAvailableSources, PROJECTS);
    }
  }, [isAuthenticated, userInfo?.email]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Measure header/footer and compute available height based on actual footer top
  useLayoutEffect(() => {
    const measure = () => {
      // removed unused: const headerEl = document.getElementById('app-header');
      if (cardRef.current) {
        const scrollY = window.scrollY || window.pageYOffset;
        const cardTop = cardRef.current.getBoundingClientRect().top + scrollY;
        const viewportH = window.innerHeight;
        const available = viewportH - (cardTop - scrollY) - GAP_ABOVE_BOTTOM;
        setCardMaxHeight(available > 280 ? available : 280);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [GAP_ABOVE_BOTTOM]);

  // Re-measure when messages change (height may shift with content)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (cardRef.current) {
        const scrollY = window.scrollY || window.pageYOffset;
        const cardTop = cardRef.current.getBoundingClientRect().top + scrollY;
        const viewportH = window.innerHeight;
        const available = viewportH - (cardTop - scrollY) - GAP_ABOVE_BOTTOM;
        setCardMaxHeight(available > 280 ? available : 280);
      }
    }, 40);
    return () => clearTimeout(timer);
  }, [messages.length, GAP_ABOVE_BOTTOM]);

  // Observe message updates in the page context
  useEffect(() => {
    if (messages.length) {
      console.log('[App] messages updated:', messages.length, 'last:', messages[messages.length - 1]);
    } else {
      console.log('[App] messages cleared');
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    const question = input.trim();
    setInput('');
    try {
      await sendMessage(selectedDataSource, question, userInfo?.email || 'system');
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Show loading during authentication
  if (authLoading || inProgress === 'login' || inProgress === 'logout') {
    return <LoadingScreen authLoading={authLoading} inProgress={inProgress} />;
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen handleSignIn={handleSignIn} />;
  }

  return (
    <div className="App">
      {/* Header separated from sidebars */}
      <Header 
        userInfo={userInfo}
        isAdmin={isAdmin}
        handleSignOut={handleSignOut}
        startNewConversation={startNewConversation}
        clearChat={clearChat}
      />

      {/* Content row: Sidebar | Main | RightRail */}
      <div className="content-row">
        <Sidebar 
          startNewConversation={startNewConversation}
          clearChat={clearChat}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          project={selectedDataSource}                    // added
          userEmail={userInfo?.email}                     // added
          onSelectConversation={(cid) => {                // added
            if (cid) loadConversation(selectedDataSource, userInfo?.email, cid);
          }}
        />

        <div className="main-area">
          <main className="main-content">
            <div className="content-breadcrumb">Homepage</div>
            
            {/* Title row with action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, mb: 2 }}>
              <h1 className="page-title" style={{ margin: 0 }}>New Conversations</h1>
              <IconButton
                aria-label="chat actions"
                onClick={handleActionsClick}
                size="small"
                sx={{
                  border: '1px solid #e5e5e5',
                  bgcolor: '#fff',
                  '&:hover': { bgcolor: '#f8f8f8' }
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={actionsAnchorEl}
                open={actionsOpen}
                onClose={handleActionsClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    elevation: 0,
                    sx: {
                      mt: 0.5,
                      border: '1px solid #e5e5e5',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      borderRadius: '10px',
                      minWidth: 160
                    }
                  }
                }}
              >
                <MenuItem onClick={handleDownload} sx={{ fontSize: 14 }}>Download</MenuItem>
                <MenuItem onClick={handleAddPeople} sx={{ fontSize: 14 }}>Add people</MenuItem>
                <MenuItem onClick={handleViewPeople} sx={{ fontSize: 14 }}>View people</MenuItem>
              </Menu>
            </Box>

            {/* Single card around conversation + textbox */}
            <Box sx={{
              maxWidth: { 
                xs: '100%', 
                md: sidebarCollapsed ? 'clamp(720px, 75vw, 1200px)' : 'clamp(580px, 60vw, 900px)'
              },
              mx: 'auto',
              width: '100%',
              mt: { xs: 1.5, md: 2 },
              mr: { xs: 0, md: sidebarCollapsed ? 0 : 1 },
              display: 'flex',
              flexDirection: 'column',
              flex: 1
            }}>
              <Paper
                ref={cardRef}
                elevation={0}
                sx={{
                  border: '1px solid #e5e5e5',
                  borderRadius: { xs: 2, md: '14px' },
                  p: { xs: 2, md: 2.5 },
                  pl: { xs: 1.5, md: 1.5 }, // further reduce left padding to match "New Conversations" alignment
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                  bgcolor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: { xs: '45vh', md: '50vh' },
                  maxHeight: cardMaxHeight ? `${cardMaxHeight}px` : 'none', // ends above footer
                  mb: { xs: 1.5, md: 2 },
                  overflow: 'hidden'
                }}
              >
                {/* Scrollable content area */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'hidden'
                    }}
                  >
                    {messages.length === 0 ? (
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <WelcomeSection userInfo={userInfo} />
                      </Box>
                    ) : (
                      <ChatContainer
                        messages={messages}
                        isLoading={isLoading}
                        chatContainerRef={chatContainerRef}
                        setInput={setInput}
                      />
                    )}
                    {error && (
                      <div className="error-banner">
                        Error: {error}
                      </div>
                    )}
                  </Box>
                </Box>
                {/* Fixed footer inside card (non-scrolling) */}
                <Box sx={{ mt: { xs: 1, md: 1 }, flexShrink: 0 }}>
                  <ChatFooter
                    input={input}
                    setInput={setInput}
                    selectedDataSource={selectedDataSource}
                    setSelectedDataSource={setSelectedDataSource}
                    availableSources={availableSources}
                    isLoading={isLoading}
                    isAdmin={isAdmin}
                    handleSendMessage={handleSendMessage}
                    handleKeyPress={handleKeyPress}
                  />
                </Box>
              </Paper>
            </Box>
          </main>

          {/* Chat footer stays in main area; source below textbox handled inside component */}
        </div>

        <RightRail
          project={selectedDataSource}
          userEmail={userInfo?.email}
          messages={messages} // added
          onSelectConversation={(cid) => {
            if (cid) loadConversation(selectedDataSource, userInfo?.email, cid);
          }}
        />
      </div>
      {showAddPeople && (
        <AddPeopleModal
          onClose={handleCloseAddPeople}
          onAdd={(name) => {
            // placeholder add action
            console.log('Added person:', name);
            handleCloseAddPeople();
          }}
        />
      )}
      {showViewPeople && (
        <ViewPeopleModal
          onClose={handleCloseViewPeople}
          participants={[
            { role: 'Owner', name: 'John Doe 1' },
            { role: 'Participant', name: 'John Doe 2' },
            { role: 'Participant', name: 'John Doe 3' },
            { role: 'Participant', name: 'John Doe 4' },
          ]}
        />
      )}
    </div>
  );
}

export default App;
